import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";
const RETENTION_BLOCKS = [1, 2, 3, 4, 5, 6];

const createEmptyForm = () => {
    const base = {
        beneficiaryId: ""
    };

    RETENTION_BLOCKS.forEach((block) => {
        base[`currentEmploymentStatus${block}`] = "";
        base[`sameOrDifferentJob${block}`] = "";
        base[`differentEmployerName${block}`] = "";
        base[`newDateOfJoining${block}`] = "";
        base[`newBeneficiaryDesignation${block}`] = "";
        base[`newIncomePerMonth${block}`] = "";
        base[`unemployedReason${block}`] = "";
        base[`retentionProofCollected${block}`] = "";
    });

    return base;
};

const toInputDate = (value) => {
    if (!value) return "";
    return String(value).slice(0, 10);
};

const boolLabel = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "-";
};

const boolToString = (value) => {
    if (value === true) return "true";
    if (value === false) return "false";
    return "";
};

const parseBooleanOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    return value === "true";
};

const parseNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const buildPayload = (form) => {
    const payload = {
        beneficiaryId: form.beneficiaryId.trim()
    };

    RETENTION_BLOCKS.forEach((block) => {
        const employmentStatus = form[`currentEmploymentStatus${block}`].trim();
        const sameOrDifferentJob = form[`sameOrDifferentJob${block}`].trim();
        const differentEmployerName = form[`differentEmployerName${block}`].trim();
        const newDateOfJoining = form[`newDateOfJoining${block}`];
        const newBeneficiaryDesignation = form[`newBeneficiaryDesignation${block}`].trim();
        const newIncomePerMonth = parseNumberOrNull(form[`newIncomePerMonth${block}`]);
        const unemployedReason = form[`unemployedReason${block}`].trim();
        const retentionProofCollected = parseBooleanOrNull(form[`retentionProofCollected${block}`]);

        payload[`currentEmploymentStatus${block}`] = employmentStatus || null;
        payload[`sameOrDifferentJob${block}`] = employmentStatus === "Working" ? (sameOrDifferentJob || null) : null;
        payload[`differentEmployerName${block}`] =
            employmentStatus === "Working" && sameOrDifferentJob === "Different Job"
                ? (differentEmployerName || null)
                : null;
        payload[`newDateOfJoining${block}`] = employmentStatus === "Working" ? (newDateOfJoining || null) : null;
        payload[`newBeneficiaryDesignation${block}`] =
            employmentStatus === "Working" ? (newBeneficiaryDesignation || null) : null;
        payload[`newIncomePerMonth${block}`] = employmentStatus === "Working" ? newIncomePerMonth : null;
        payload[`unemployedReason${block}`] = employmentStatus === "Unemployed" ? (unemployedReason || null) : null;
        payload[`retentionProofCollected${block}`] = retentionProofCollected;
    });

    return payload;
};

const normalizeFromApi = (data) => {
    const normalized = {
        beneficiaryId: data.beneficiaryId || ""
    };

    RETENTION_BLOCKS.forEach((block) => {
        normalized[`currentEmploymentStatus${block}`] = data[`currentEmploymentStatus${block}`] || "";
        normalized[`sameOrDifferentJob${block}`] = data[`sameOrDifferentJob${block}`] || "";
        normalized[`differentEmployerName${block}`] = data[`differentEmployerName${block}`] || "";
        normalized[`newDateOfJoining${block}`] = toInputDate(data[`newDateOfJoining${block}`]);
        normalized[`newBeneficiaryDesignation${block}`] = data[`newBeneficiaryDesignation${block}`] || "";
        normalized[`newIncomePerMonth${block}`] = data[`newIncomePerMonth${block}`] ?? "";
        normalized[`unemployedReason${block}`] = data[`unemployedReason${block}`] || "";
        normalized[`retentionProofCollected${block}`] = boolToString(data[`retentionProofCollected${block}`]);
    });

    return normalized;
};

const getRetentionSummary = (row, block) => {
    const status = row[`currentEmploymentStatus${block}`];

    if (!status) return "-";

    if (status === "Working") {
        const jobType = row[`sameOrDifferentJob${block}`] || "-";
        const designation = row[`newBeneficiaryDesignation${block}`] || "-";
        const income = row[`newIncomePerMonth${block}`] ?? "-";
        return `${status} / ${jobType} / ${designation} / ${income}`;
    }

    if (status === "Unemployed") {
        return `${status} / ${row[`unemployedReason${block}`] || "-"}`;
    }

    return status;
};

export default function CandidateRetention() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "candidateRetentions";

    const [msg, setMsg] = useState({ type: "", text: "" });
    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [loadingAll, setLoadingAll] = useState(false);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingBeneficiaryId, setEditingBeneficiaryId] = useState("");
    const [form, setForm] = useState(createEmptyForm());

    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`
        }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }),
        [token]
    );

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    };

    const fetchCandidateRetentions = useCallback(async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);

        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, "/api/candidate-retentions");
            const res = await fetch(scopedUrl + (scopedUrl.includes("?") ? "&" : "?") + "page=0&size=100", {
                headers: authHeaders
            });

            const data = await safeJson(res);
            const list = data.content || data || [];
            setRows(list);
            store.setRows(CACHE_KEY, list, false);
            setAllLoaded(false);
        } catch {
            setRows([]);
            flash("err", "Failed to load candidate retentions");
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    const fetchAllCandidateRetentions = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/candidate-retentions"), {
                headers: authHeaders
            });
            const data = await safeJson(res);
            const list = data.content || data || [];
            setRows(list);
            store.setRows(CACHE_KEY, list, true);
            setAllLoaded(true);
        } catch {
            flash("err", "Failed to load all candidate retentions");
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchCandidateRetentions();
    }, [fetchCandidateRetentions]);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.candidateRetentions ?? null); })
            .catch(() => { });
    }, []);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (!q) return scopedRows;

        return scopedRows.filter((row) => {
            if (row.beneficiaryId?.toLowerCase().includes(q) || row.beneficiaryName?.toLowerCase().includes(q)) {
                return true;
            }

            return RETENTION_BLOCKS.some((block) =>
                row[`currentEmploymentStatus${block}`]?.toLowerCase().includes(q) ||
                row[`sameOrDifferentJob${block}`]?.toLowerCase().includes(q) ||
                row[`differentEmployerName${block}`]?.toLowerCase().includes(q) ||
                row[`newBeneficiaryDesignation${block}`]?.toLowerCase().includes(q) ||
                row[`unemployedReason${block}`]?.toLowerCase().includes(q)
            );
        });
    }, [scopedRows, search]);

    const resetForm = () => {
        setForm(createEmptyForm());
        setEditingBeneficiaryId("");
    };

    const openCreate = () => {
        resetForm();
        setModalOpen(true);
    };

    const openEdit = async (beneficiaryId) => {
        if (!beneficiaryId) return;

        setSaving(false);
        setModalOpen(true);
        setEditingBeneficiaryId(beneficiaryId);

        try {
            const res = await fetch(
                `${BASE_URL}/api/candidate-retentions/${encodeURIComponent(beneficiaryId)}`,
                { headers: authHeaders }
            );

            if (!res.ok) {
                throw new Error("Failed to fetch record");
            }

            const data = await safeJson(res);
            setForm(normalizeFromApi(data));
        } catch {
            flash("err", "Failed to load record details");
            setModalOpen(false);
            resetForm();
        }
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;

        setForm((prev) => {
            const next = {
                ...prev,
                [name]: value
            };

            const block = RETENTION_BLOCKS.find((item) => name.endsWith(String(item)));

            if (!block) return next;

            if (name === `currentEmploymentStatus${block}`) {
                if (value !== "Working") {
                    next[`sameOrDifferentJob${block}`] = "";
                    next[`differentEmployerName${block}`] = "";
                    next[`newDateOfJoining${block}`] = "";
                    next[`newBeneficiaryDesignation${block}`] = "";
                    next[`newIncomePerMonth${block}`] = "";
                }
                if (value !== "Unemployed") {
                    next[`unemployedReason${block}`] = "";
                }
            }

            if (name === `sameOrDifferentJob${block}` && value !== "Different Job") {
                next[`differentEmployerName${block}`] = "";
            }

            return next;
        });
    };

    const saveCandidateRetention = async () => {
        if (!form.beneficiaryId.trim()) {
            flash("err", "Beneficiary ID is required");
            return;
        }

        setSaving(true);

        try {
            const payload = buildPayload(form);

            const res = await fetch(`${BASE_URL}/api/candidate-retentions`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                throw new Error("Save failed");
            }

            flash("ok", editingBeneficiaryId ? "Candidate retention updated" : "Candidate retention created");
            setModalOpen(false);
            resetForm();
            store.invalidate(CACHE_KEY);
            fetchCandidateRetentions();
        } catch {
            flash("err", "Failed to save candidate retention");
        } finally {
            setSaving(false);
        }
    };

    const deleteCandidateRetention = async (id) => {
        if (!id) {
            flash("err", "Record ID not found");
            return;
        }

        if (!window.confirm("Delete this candidate retention record?")) return;

        try {
            const res = await fetch(`${BASE_URL}/api/candidate-retentions/${id}`, {
                method: "DELETE",
                headers: authHeaders
            });

            if (!res.ok) {
                throw new Error("Delete failed");
            }

            flash("ok", "Candidate retention deleted");
            store.invalidate(CACHE_KEY);
            fetchCandidateRetentions();
        } catch {
            flash("err", "Failed to delete record");
        }
    };

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");
        let dataRows;
        let headerRow;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/candidate-retentions/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchCandidateRetentions();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // const downloadTemplate = () => {
    //     const headers = ["Beneficiary ID", "Beneficiary Name"];

    //     RETENTION_BLOCKS.forEach((block) => {
    //         headers.push(`Current Employment status_${block}`);
    //         headers.push(`If working, then whether in the same job or different job_${block}`);
    //         headers.push(`If in a different job, Employer name/Self-employed_${block}`);
    //         headers.push(`New Date of joining_${block}`);
    //         headers.push(`New Designation of Beneficiary_${block}`);
    //         headers.push(`New Income per month_${block}`);
    //         headers.push(`If unemployed, reason_${block}`);
    //         headers.push(`Retention proof collected_${block}`);
    //     });

    //     const sampleRow = [
    //         "EP/CN-00001",
    //         "Mintal Kumari",
    //         "Working",
    //         "Same Job",
    //         "",
    //         "01/06/2024",
    //         "DEO",
    //         16000,
    //         "",
    //         "Yes",
    //         "Working",
    //         "Different Job",
    //         "ABC Pvt Ltd",
    //         "10/07/2024",
    //         "Senior DEO",
    //         18000,
    //         "",
    //         "Yes",
    //         "Unemployed",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "Left job",
    //         "No",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         "",
    //         ""
    //     ];

    //     const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    //     const workbook = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(workbook, worksheet, "Candidate Retention");
    //     XLSX.writeFile(workbook, "candidate_retention_template.xlsx");
    // };

    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = rows;
            if (!allLoaded) {
                const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/candidate-retentions"), { headers: authHeaders });
                const json = await safeJson(res);
                const all = Array.isArray(json) ? json : (json?.content ?? []);
                data = all;
                setRows(all);
                setAllLoaded(true);
                setVisibleCount(all.length);
                store.setRows(CACHE_KEY, all, true);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "CandidateRetention");
            XLSX.writeFile(wb, "candidate_retention.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/candidate_retention_template.xlsx"; // local path
        a.download = "candidate_retention_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <div className="cmp-page">
            <div className="header">
                <h1>Candidate Retention</h1>
                {/* <button className="btn btn-primary" onClick={openCreate}>
                    Add Record
                </button> */}
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            <div className="card grid-3">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                />

                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>

                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>
            </div>

            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search beneficiary / employer / designation / reason..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        disabled={exporting}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {/* <th>ID</th> */}
                                <th>Beneficiary ID</th>
                                <th>Beneficiary Name</th>
                                {RETENTION_BLOCKS.map((block) => (
                                    <th key={block}>{`Retention ${block}`}</th>
                                ))}
                                <th>Batch ID</th>
                                <th>Center ID</th>
                                <th>Project ID</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.slice(0, visibleCount).map((row, i) => (
                                <tr key={row.id || row.beneficiaryId}>
                                    <td>{i + 1}</td>
                                    {/* <td>{row.id ?? "-"}</td> */}
                                    <td>{row.beneficiaryId || "-"}</td>
                                    <td>{row.beneficiaryName || "-"}</td>
                                    {RETENTION_BLOCKS.map((block) => (
                                        <td key={block}>
                                            <div>{getRetentionSummary(row, block)}</div>
                                            <div className="mini">
                                                {`Proof: ${boolLabel(row[`retentionProofCollected${block}`])}`}
                                            </div>
                                        </td>
                                    ))}
                                    <td>{row.batchId || "-"}</td>
                                    <td>{row.centerId || "-"}</td>
                                    <td>{row.projectId || "-"}</td>
                                    <td className="actions">
                                        {/* <button
                                            className="btn btn-sm"
                                            onClick={() => openEdit(row.beneficiaryId)}
                                        >
                                            Edit
                                        </button> */}
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteCandidateRetention(row.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="15" className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan="15" style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllCandidateRetentions} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="15" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Load More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modalOpen && createPortal(
                <div className="modal-overlay" onClick={() => {
                    setModalOpen(false);
                    resetForm();
                }}>
                    <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-title">
                            {editingBeneficiaryId ? "Edit Candidate Retention" : "Add Candidate Retention"}
                        </div>

                        <div className="form-grid one">
                            <div className="field">
                                <label>Beneficiary ID</label>
                                <input
                                    name="beneficiaryId"
                                    value={form.beneficiaryId}
                                    onChange={handleFormChange}
                                    disabled={!!editingBeneficiaryId}
                                    placeholder="EP/CN-00001"
                                />
                            </div>
                        </div>

                        {RETENTION_BLOCKS.map((block) => {
                            const employmentStatus = form[`currentEmploymentStatus${block}`];
                            const sameOrDifferentJob = form[`sameOrDifferentJob${block}`];
                            const isWorking = employmentStatus === "Working";
                            const isUnemployed = employmentStatus === "Unemployed";
                            const isDifferentJob = sameOrDifferentJob === "Different Job";

                            return (
                                <div className="section-card" key={block}>
                                    <div className="section-title">{`Retention ${block}`}</div>

                                    <div className="form-grid">
                                        <div className="field">
                                            <label>Current Employment Status</label>
                                            <select
                                                name={`currentEmploymentStatus${block}`}
                                                value={form[`currentEmploymentStatus${block}`]}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select</option>
                                                <option value="Working">Working</option>
                                                <option value="Unemployed">Unemployed</option>
                                            </select>
                                        </div>

                                        <div className="field">
                                            <label>Same or Different Job</label>
                                            <select
                                                name={`sameOrDifferentJob${block}`}
                                                value={form[`sameOrDifferentJob${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isWorking}
                                            >
                                                <option value="">Select</option>
                                                <option value="Same Job">Same Job</option>
                                                <option value="Different Job">Different Job</option>
                                            </select>
                                        </div>

                                        <div className="field">
                                            <label>Different Employer Name</label>
                                            <input
                                                name={`differentEmployerName${block}`}
                                                value={form[`differentEmployerName${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isWorking || !isDifferentJob}
                                                placeholder="ABC Pvt Ltd"
                                            />
                                        </div>

                                        <div className="field">
                                            <label>New Date of Joining</label>
                                            <input
                                                type="date"
                                                name={`newDateOfJoining${block}`}
                                                value={form[`newDateOfJoining${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isWorking}
                                            />
                                        </div>

                                        <div className="field">
                                            <label>New Beneficiary Designation</label>
                                            <input
                                                name={`newBeneficiaryDesignation${block}`}
                                                value={form[`newBeneficiaryDesignation${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isWorking}
                                                placeholder="DEO"
                                            />
                                        </div>

                                        <div className="field">
                                            <label>New Income per Month</label>
                                            <input
                                                type="number"
                                                min="0"
                                                name={`newIncomePerMonth${block}`}
                                                value={form[`newIncomePerMonth${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isWorking}
                                                placeholder="16000"
                                            />
                                        </div>

                                        <div className="field">
                                            <label>Unemployed Reason</label>
                                            <input
                                                name={`unemployedReason${block}`}
                                                value={form[`unemployedReason${block}`]}
                                                onChange={handleFormChange}
                                                disabled={!isUnemployed}
                                                placeholder="Left job"
                                            />
                                        </div>

                                        <div className="field">
                                            <label>Retention Proof Collected</label>
                                            <select
                                                name={`retentionProofCollected${block}`}
                                                value={form[`retentionProofCollected${block}`]}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select</option>
                                                <option value="true">Yes</option>
                                                <option value="false">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="modal-actions">
                            <button
                                className="btn"
                                onClick={() => {
                                    setModalOpen(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={saveCandidateRetention} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
.cmp-page{
padding:22px;
background:#f7f7fb;
min-height:100vh;
font-family:Inter
}

.header{
display:flex;
justify-content:space-between;
align-items:center;
gap:12px
}

h1{
font-size:26px;
font-weight:900
}

.flash{
padding:10px;
border-radius:10px;
margin:10px 0;
font-weight:700
}

.flash-ok{
background:#ecfdf5;
color:#065f46
}

.flash-err{
background:#fef2f2;
color:#991b1b
}

.card{
background:#fff;
border:1px solid #e5e7eb;
border-radius:14px;
padding:16px;
margin-bottom:14px
}

.grid-3{
display:grid;
grid-template-columns:1fr auto auto;
gap:10px
}

.btn{
border:1px solid #e5e7eb;
padding:10px 14px;
border-radius:12px;
font-weight:800;
cursor:pointer;
background:#fff
}

.btn:disabled{
opacity:.65;
cursor:not-allowed
}

.btn-primary{
background:#2563eb;
color:#fff
}

.btn-outline{
background:#fff
}

.btn-sm{
padding:6px 10px;
font-size:12px;
margin-right:8px
}

.btn-danger{
color:#991b1b;
border-color:#fca5a5
}

.search{
margin-bottom:10px;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb;
width:100%
}

.table-wrap{
overflow-x:auto
}

.ep-table{
width:100%;
min-width:2400px;
border-collapse:collapse
}

th,td{
padding:10px;
border-bottom:1px solid #e5e7eb;
font-size:13px;
white-space:nowrap;
vertical-align:top
}

th{
background:#fcfcff;
text-align:left
}

td.actions{
position:sticky;
right:0;
background:#fff
}

.mini{
margin-top:4px;
font-size:11px;
color:#6b7280;
font-weight:700
}

.muted{
color:#6b7280;
font-weight:700;
text-align:center
}

.modal-overlay{
position:fixed;
inset:0;
background:rgba(0,0,0,.45);
display:flex;
align-items:center;
justify-content:center;
z-index:99999
}

.ep-modal{
background:#fff;
padding:18px;
border-radius:16px;
width:520px;
max-height:90vh;
overflow:auto
}

.ep-modal.wide{
width:min(1180px,92vw)
}

.modal-title{
font-size:18px;
font-weight:900;
margin-bottom:14px
}

.section-card{
border:1px solid #e5e7eb;
border-radius:14px;
padding:14px;
margin-bottom:14px;
background:#fafbff
}

.section-title{
font-size:15px;
font-weight:900;
margin-bottom:12px
}

.form-grid{
display:grid;
grid-template-columns:repeat(2, minmax(0, 1fr));
gap:12px
}

.form-grid.one{
grid-template-columns:1fr;
margin-bottom:14px
}

.field{
display:flex;
flex-direction:column;
gap:6px
}

.field label{
font-size:12px;
font-weight:800;
color:#374151
}

.ep-modal input,
.ep-modal select{
width:100%;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb
}

.modal-actions{
display:flex;
justify-content:flex-end;
gap:10px;
margin-top:16px
}

@media (max-width: 768px){
  .grid-3{
    grid-template-columns:1fr
  }

  .form-grid{
    grid-template-columns:1fr
  }

  .btn-sm{
    margin-right:6px;
    margin-bottom:6px
  }
}
`}</style>
        </div>
    );
}


// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
// import useDataStore from "../../store/dataStore";

// const BASE_URL = "https://mis.empowerplus.in";
// const RETENTION_BLOCKS = [1, 2, 3, 4, 5, 6];

// const createEmptyForm = () => {
//     const base = {
//         beneficiaryId: ""
//     };

//     RETENTION_BLOCKS.forEach((block) => {
//         base[`currentEmploymentStatus${block}`] = "";
//         base[`sameOrDifferentJob${block}`] = "";
//         base[`differentEmployerName${block}`] = "";
//         base[`newDateOfJoining${block}`] = "";
//         base[`newBeneficiaryDesignation${block}`] = "";
//         base[`newIncomePerMonth${block}`] = "";
//         base[`unemployedReason${block}`] = "";
//         base[`retentionProofCollected${block}`] = "";
//     });

//     return base;
// };

// const toInputDate = (value) => {
//     if (!value) return "";
//     return String(value).slice(0, 10);
// };

// const boolLabel = (value) => {
//     if (value === true) return "Yes";
//     if (value === false) return "No";
//     return "-";
// };

// const boolToString = (value) => {
//     if (value === true) return "true";
//     if (value === false) return "false";
//     return "";
// };

// const parseBooleanOrNull = (value) => {
//     if (value === "" || value === null || value === undefined) return null;
//     return value === "true";
// };

// const parseNumberOrNull = (value) => {
//     if (value === "" || value === null || value === undefined) return null;
//     const parsed = Number(value);
//     return Number.isNaN(parsed) ? null : parsed;
// };

// const buildPayload = (form) => {
//     const payload = {
//         beneficiaryId: form.beneficiaryId.trim()
//     };

//     RETENTION_BLOCKS.forEach((block) => {
//         const employmentStatus = form[`currentEmploymentStatus${block}`].trim();
//         const sameOrDifferentJob = form[`sameOrDifferentJob${block}`].trim();
//         const differentEmployerName = form[`differentEmployerName${block}`].trim();
//         const newDateOfJoining = form[`newDateOfJoining${block}`];
//         const newBeneficiaryDesignation = form[`newBeneficiaryDesignation${block}`].trim();
//         const newIncomePerMonth = parseNumberOrNull(form[`newIncomePerMonth${block}`]);
//         const unemployedReason = form[`unemployedReason${block}`].trim();
//         const retentionProofCollected = parseBooleanOrNull(form[`retentionProofCollected${block}`]);

//         payload[`currentEmploymentStatus${block}`] = employmentStatus || null;
//         payload[`sameOrDifferentJob${block}`] = employmentStatus === "Working" ? (sameOrDifferentJob || null) : null;
//         payload[`differentEmployerName${block}`] =
//             employmentStatus === "Working" && sameOrDifferentJob === "Different Job"
//                 ? (differentEmployerName || null)
//                 : null;
//         payload[`newDateOfJoining${block}`] = employmentStatus === "Working" ? (newDateOfJoining || null) : null;
//         payload[`newBeneficiaryDesignation${block}`] =
//             employmentStatus === "Working" ? (newBeneficiaryDesignation || null) : null;
//         payload[`newIncomePerMonth${block}`] = employmentStatus === "Working" ? newIncomePerMonth : null;
//         payload[`unemployedReason${block}`] = employmentStatus === "Unemployed" ? (unemployedReason || null) : null;
//         payload[`retentionProofCollected${block}`] = retentionProofCollected;
//     });

//     return payload;
// };

// const normalizeFromApi = (data) => {
//     const normalized = {
//         beneficiaryId: data.beneficiaryId || ""
//     };

//     RETENTION_BLOCKS.forEach((block) => {
//         normalized[`currentEmploymentStatus${block}`] = data[`currentEmploymentStatus${block}`] || "";
//         normalized[`sameOrDifferentJob${block}`] = data[`sameOrDifferentJob${block}`] || "";
//         normalized[`differentEmployerName${block}`] = data[`differentEmployerName${block}`] || "";
//         normalized[`newDateOfJoining${block}`] = toInputDate(data[`newDateOfJoining${block}`]);
//         normalized[`newBeneficiaryDesignation${block}`] = data[`newBeneficiaryDesignation${block}`] || "";
//         normalized[`newIncomePerMonth${block}`] = data[`newIncomePerMonth${block}`] ?? "";
//         normalized[`unemployedReason${block}`] = data[`unemployedReason${block}`] || "";
//         normalized[`retentionProofCollected${block}`] = boolToString(data[`retentionProofCollected${block}`]);
//     });

//     return normalized;
// };

// const getRetentionSummary = (row, block) => {
//     const status = row[`currentEmploymentStatus${block}`];

//     if (!status) return "-";

//     if (status === "Working") {
//         const jobType = row[`sameOrDifferentJob${block}`] || "-";
//         const designation = row[`newBeneficiaryDesignation${block}`] || "-";
//         const income = row[`newIncomePerMonth${block}`] ?? "-";
//         return `${status} / ${jobType} / ${designation} / ${income}`;
//     }

//     if (status === "Unemployed") {
//         return `${status} / ${row[`unemployedReason${block}`] || "-"}`;
//     }

//     return status;
// };

// export default function CandidateRetention() {
//     const token = localStorage.getItem("token");
//     const store = useDataStore();
//     const CACHE_KEY = "candidateRetentions";

//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
//     const [loading, setLoading] = useState(false);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);
//     const [totalCount, setTotalCount] = useState(null);
//     const [modalOpen, setModalOpen] = useState(false);
//     const [saving, setSaving] = useState(false);
//     const [editingBeneficiaryId, setEditingBeneficiaryId] = useState("");
//     const [form, setForm] = useState(createEmptyForm());

//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`
//         }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//         }),
//         [token]
//     );

//     const safeJson = async (res) => {
//         const text = await res.text();
//         try {
//             return text ? JSON.parse(text) : [];
//         } catch {
//             return [];
//         }
//     };

//     const fetchCandidateRetentions = useCallback(async () => {
//         if (store.isCached(CACHE_KEY)) {
//             setRows(store.getRows(CACHE_KEY));
//             return;
//         }
//         setLoading(true);

//         try {
//             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/candidate-retentions"), {
//                 headers: authHeaders
//             });

//             const data = await safeJson(res);
//             const list = data.content || data || [];
//             setRows(list);
//             store.setRows(CACHE_KEY, list, true);
//         } catch {
//             setRows([]);
//             flash("err", "Failed to load candidate retentions");
//         } finally {
//             setLoading(false);
//         }
//     }, [authHeaders]);

//     useEffect(() => {
//         fetchCandidateRetentions();
//     }, [fetchCandidateRetentions]);

//     useEffect(() => {
//         fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
//             .then(r => r.ok ? r.json() : null)
//             .then(d => { if (d) setTotalCount(d.candidateRetentions ?? null); })
//             .catch(() => {});
//     }, []);

//     useEffect(() => { setVisibleCount(100); }, [search]);

//     const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

//     const filtered = useMemo(() => {
//         const q = search.trim().toLowerCase();

//         if (!q) return scopedRows;

//         return scopedRows.filter((row) => {
//             if (row.beneficiaryId?.toLowerCase().includes(q) || row.beneficiaryName?.toLowerCase().includes(q)) {
//                 return true;
//             }

//             return RETENTION_BLOCKS.some((block) =>
//                 row[`currentEmploymentStatus${block}`]?.toLowerCase().includes(q) ||
//                 row[`sameOrDifferentJob${block}`]?.toLowerCase().includes(q) ||
//                 row[`differentEmployerName${block}`]?.toLowerCase().includes(q) ||
//                 row[`newBeneficiaryDesignation${block}`]?.toLowerCase().includes(q) ||
//                 row[`unemployedReason${block}`]?.toLowerCase().includes(q)
//             );
//         });
//     }, [scopedRows, search]);

//     const resetForm = () => {
//         setForm(createEmptyForm());
//         setEditingBeneficiaryId("");
//     };

//     const openCreate = () => {
//         resetForm();
//         setModalOpen(true);
//     };

//     const openEdit = async (beneficiaryId) => {
//         if (!beneficiaryId) return;

//         setSaving(false);
//         setModalOpen(true);
//         setEditingBeneficiaryId(beneficiaryId);

//         try {
//             const res = await fetch(
//                 `${BASE_URL}/api/candidate-retentions/${encodeURIComponent(beneficiaryId)}`,
//                 { headers: authHeaders }
//             );

//             if (!res.ok) {
//                 throw new Error("Failed to fetch record");
//             }

//             const data = await safeJson(res);
//             setForm(normalizeFromApi(data));
//         } catch {
//             flash("err", "Failed to load record details");
//             setModalOpen(false);
//             resetForm();
//         }
//     };

//     const handleFormChange = (event) => {
//         const { name, value } = event.target;

//         setForm((prev) => {
//             const next = {
//                 ...prev,
//                 [name]: value
//             };

//             const block = RETENTION_BLOCKS.find((item) => name.endsWith(String(item)));

//             if (!block) return next;

//             if (name === `currentEmploymentStatus${block}`) {
//                 if (value !== "Working") {
//                     next[`sameOrDifferentJob${block}`] = "";
//                     next[`differentEmployerName${block}`] = "";
//                     next[`newDateOfJoining${block}`] = "";
//                     next[`newBeneficiaryDesignation${block}`] = "";
//                     next[`newIncomePerMonth${block}`] = "";
//                 }
//                 if (value !== "Unemployed") {
//                     next[`unemployedReason${block}`] = "";
//                 }
//             }

//             if (name === `sameOrDifferentJob${block}` && value !== "Different Job") {
//                 next[`differentEmployerName${block}`] = "";
//             }

//             return next;
//         });
//     };

//     const saveCandidateRetention = async () => {
//         if (!form.beneficiaryId.trim()) {
//             flash("err", "Beneficiary ID is required");
//             return;
//         }

//         setSaving(true);

//         try {
//             const payload = buildPayload(form);

//             const res = await fetch(`${BASE_URL}/api/candidate-retentions`, {
//                 method: "POST",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(payload)
//             });

//             if (!res.ok) {
//                 throw new Error("Save failed");
//             }

//             flash("ok", editingBeneficiaryId ? "Candidate retention updated" : "Candidate retention created");
//             setModalOpen(false);
//             resetForm();
//             store.invalidate(CACHE_KEY);
//             fetchCandidateRetentions();
//         } catch {
//             flash("err", "Failed to save candidate retention");
//         } finally {
//             setSaving(false);
//         }
//     };

//     const deleteCandidateRetention = async (id) => {
//         if (!id) {
//             flash("err", "Record ID not found");
//             return;
//         }

//         if (!window.confirm("Delete this candidate retention record?")) return;

//         try {
//             const res = await fetch(`${BASE_URL}/api/candidate-retentions/${id}`, {
//                 method: "DELETE",
//                 headers: authHeaders
//             });

//             if (!res.ok) {
//                 throw new Error("Delete failed");
//             }

//             flash("ok", "Candidate retention deleted");
//             store.invalidate(CACHE_KEY);
//             fetchCandidateRetentions();
//         } catch {
//             flash("err", "Failed to delete record");
//         }
//     };

//     const CHUNK_SIZE = 5000;
//     const PARALLEL = 6;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");
//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1);
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }
//         if (!dataRows.length) return flash("err", "Excel file is empty");
//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
//         let uploadedSoFar = 0;
//         let totalInserted = 0;
//         let totalSkipped = 0;
//         let failed = false;
//         let firstError = "";
//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
//             const res = await fetch(`${BASE_URL}/api/candidate-retentions/upload-json?actor=Rahul`, {
//                 method: "POST",
//                 headers: { ...authHeaders, "Content-Type": "application/json" },
//                 body: JSON.stringify(chunkRows),
//             });
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const text = await res.text();
//             let data;
//             try { data = JSON.parse(text); } catch { data = text; }
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
//             return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
//         };
//         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
//             const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
//             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
//             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
//             for (const result of results) {
//                 if (result.status === "fulfilled") {
//                     totalInserted += result.value.inserted;
//                     totalSkipped += result.value.skipped;
//                     uploadedSoFar += result.value.inserted + result.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = result.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
//         }
//         setUploading(false);
//         if (!failed) {
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
//             setFile(null);
//             setUploadProgress(null);
//             store.invalidate(CACHE_KEY);
//             fetchCandidateRetentions();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     // const downloadTemplate = () => {
//     //     const headers = ["Beneficiary ID", "Beneficiary Name"];

//     //     RETENTION_BLOCKS.forEach((block) => {
//     //         headers.push(`Current Employment status_${block}`);
//     //         headers.push(`If working, then whether in the same job or different job_${block}`);
//     //         headers.push(`If in a different job, Employer name/Self-employed_${block}`);
//     //         headers.push(`New Date of joining_${block}`);
//     //         headers.push(`New Designation of Beneficiary_${block}`);
//     //         headers.push(`New Income per month_${block}`);
//     //         headers.push(`If unemployed, reason_${block}`);
//     //         headers.push(`Retention proof collected_${block}`);
//     //     });

//     //     const sampleRow = [
//     //         "EP/CN-00001",
//     //         "Mintal Kumari",
//     //         "Working",
//     //         "Same Job",
//     //         "",
//     //         "01/06/2024",
//     //         "DEO",
//     //         16000,
//     //         "",
//     //         "Yes",
//     //         "Working",
//     //         "Different Job",
//     //         "ABC Pvt Ltd",
//     //         "10/07/2024",
//     //         "Senior DEO",
//     //         18000,
//     //         "",
//     //         "Yes",
//     //         "Unemployed",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "Left job",
//     //         "No",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         "",
//     //         ""
//     //     ];

//     //     const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
//     //     const workbook = XLSX.utils.book_new();
//     //     XLSX.utils.book_append_sheet(workbook, worksheet, "Candidate Retention");
//     //     XLSX.writeFile(workbook, "candidate_retention_template.xlsx");
//     // };

//     const [exporting, setExporting] = useState(false);

//     const exportExcel = () => {
//         if (!rows.length) return flash("err", "No data to export");
//         if (exporting) return;
//         setExporting(true);
//         setTimeout(() => {
//             try {
//                 const ws = XLSX.utils.json_to_sheet(rows);
//                 const wb = XLSX.utils.book_new();
//                 XLSX.utils.book_append_sheet(wb, ws, "CandidateRetention");
//                 XLSX.writeFile(wb, "candidate_retention.xlsx");
//             } finally {
//                 setExporting(false);
//             }
//         }, 50);
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/candidate_retention_template.xlsx"; // local path
//         a.download = "candidate_retention_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     return (
//         <div className="cmp-page">
//             <div className="header">
//                 <h1>Candidate Retention</h1>
//                 {/* <button className="btn btn-primary" onClick={openCreate}>
//                     Add Record
//                 </button> */}
//             </div>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {uploadProgress && (
//                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                             {uploadProgress.error
//                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                 : uploading
//                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                     : `Upload complete`}
//                         </span>
//                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                         </span>
//                     </div>
//                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                             borderRadius: 999,
//                             transition: "width 0.3s ease",
//                         }} />
//                     </div>
//                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                     </div>
//                 </div>
//             )}

//             <div className="card grid-3">
//                 <input
//                     type="file"
//                     accept=".xlsx,.xls"
//                     onChange={(e) => setFile(e.target.files[0] || null)}
//                 />

//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                 </button>

//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>
//             </div>

//             <div className="card">
//                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
//                     <input
//                         className="search"
//                         placeholder="Search beneficiary / employer / designation / reason..."
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                         style={{ flex: "0 0 70%", marginBottom: 0 }}
//                     />
//                     <button
//                         className="btn btn-outline"
//                         onClick={exportExcel}
//                         style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                         disabled={exporting}
//                     >
//                         {exporting ? "Processing..." : "Export All Data"}
//                     </button>
//                 </div>
//                 {totalCount !== null && (
//                     <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
//                         Total: {totalCount.toLocaleString()}
//                     </div>
//                 )}

//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>#</th>
//                                 <th>ID</th>
//                                 <th>Beneficiary ID</th>
//                                 <th>Beneficiary Name</th>
//                                 {RETENTION_BLOCKS.map((block) => (
//                                     <th key={block}>{`Retention ${block}`}</th>
//                                 ))}
//                                 <th>Batch ID</th>
//                                 <th>Center ID</th>
//                                 <th>Project ID</th>
//                                 <th className="actions">Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.slice(0, visibleCount).map((row, i) => (
//                                 <tr key={row.id || row.beneficiaryId}>
//                                     <td>{i + 1}</td>
//                                     <td>{row.id ?? "-"}</td>
//                                     <td>{row.beneficiaryId || "-"}</td>
//                                     <td>{row.beneficiaryName || "-"}</td>
//                                     {RETENTION_BLOCKS.map((block) => (
//                                         <td key={block}>
//                                             <div>{getRetentionSummary(row, block)}</div>
//                                             <div className="mini">
//                                                 {`Proof: ${boolLabel(row[`retentionProofCollected${block}`])}`}
//                                             </div>
//                                         </td>
//                                     ))}
//                                     <td>{row.batchId || "-"}</td>
//                                     <td>{row.centerId || "-"}</td>
//                                     <td>{row.projectId || "-"}</td>
//                                     <td className="actions">
//                                         {/* <button
//                                             className="btn btn-sm"
//                                             onClick={() => openEdit(row.beneficiaryId)}
//                                         >
//                                             Edit
//                                         </button> */}
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteCandidateRetention(row.id)}
//                                         >
//                                             Delete
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="15" className="muted">
//                                         {loading ? "Loading..." : "No records found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="15" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 100)}
//                                         >
//                                             Load More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>

//             {modalOpen && createPortal(
//                 <div className="modal-overlay" onClick={() => {
//                     setModalOpen(false);
//                     resetForm();
//                 }}>
//                     <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                         <div className="modal-title">
//                             {editingBeneficiaryId ? "Edit Candidate Retention" : "Add Candidate Retention"}
//                         </div>

//                         <div className="form-grid one">
//                             <div className="field">
//                                 <label>Beneficiary ID</label>
//                                 <input
//                                     name="beneficiaryId"
//                                     value={form.beneficiaryId}
//                                     onChange={handleFormChange}
//                                     disabled={!!editingBeneficiaryId}
//                                     placeholder="EP/CN-00001"
//                                 />
//                             </div>
//                         </div>

//                         {RETENTION_BLOCKS.map((block) => {
//                             const employmentStatus = form[`currentEmploymentStatus${block}`];
//                             const sameOrDifferentJob = form[`sameOrDifferentJob${block}`];
//                             const isWorking = employmentStatus === "Working";
//                             const isUnemployed = employmentStatus === "Unemployed";
//                             const isDifferentJob = sameOrDifferentJob === "Different Job";

//                             return (
//                                 <div className="section-card" key={block}>
//                                     <div className="section-title">{`Retention ${block}`}</div>

//                                     <div className="form-grid">
//                                         <div className="field">
//                                             <label>Current Employment Status</label>
//                                             <select
//                                                 name={`currentEmploymentStatus${block}`}
//                                                 value={form[`currentEmploymentStatus${block}`]}
//                                                 onChange={handleFormChange}
//                                             >
//                                                 <option value="">Select</option>
//                                                 <option value="Working">Working</option>
//                                                 <option value="Unemployed">Unemployed</option>
//                                             </select>
//                                         </div>

//                                         <div className="field">
//                                             <label>Same or Different Job</label>
//                                             <select
//                                                 name={`sameOrDifferentJob${block}`}
//                                                 value={form[`sameOrDifferentJob${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isWorking}
//                                             >
//                                                 <option value="">Select</option>
//                                                 <option value="Same Job">Same Job</option>
//                                                 <option value="Different Job">Different Job</option>
//                                             </select>
//                                         </div>

//                                         <div className="field">
//                                             <label>Different Employer Name</label>
//                                             <input
//                                                 name={`differentEmployerName${block}`}
//                                                 value={form[`differentEmployerName${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isWorking || !isDifferentJob}
//                                                 placeholder="ABC Pvt Ltd"
//                                             />
//                                         </div>

//                                         <div className="field">
//                                             <label>New Date of Joining</label>
//                                             <input
//                                                 type="date"
//                                                 name={`newDateOfJoining${block}`}
//                                                 value={form[`newDateOfJoining${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isWorking}
//                                             />
//                                         </div>

//                                         <div className="field">
//                                             <label>New Beneficiary Designation</label>
//                                             <input
//                                                 name={`newBeneficiaryDesignation${block}`}
//                                                 value={form[`newBeneficiaryDesignation${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isWorking}
//                                                 placeholder="DEO"
//                                             />
//                                         </div>

//                                         <div className="field">
//                                             <label>New Income per Month</label>
//                                             <input
//                                                 type="number"
//                                                 min="0"
//                                                 name={`newIncomePerMonth${block}`}
//                                                 value={form[`newIncomePerMonth${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isWorking}
//                                                 placeholder="16000"
//                                             />
//                                         </div>

//                                         <div className="field">
//                                             <label>Unemployed Reason</label>
//                                             <input
//                                                 name={`unemployedReason${block}`}
//                                                 value={form[`unemployedReason${block}`]}
//                                                 onChange={handleFormChange}
//                                                 disabled={!isUnemployed}
//                                                 placeholder="Left job"
//                                             />
//                                         </div>

//                                         <div className="field">
//                                             <label>Retention Proof Collected</label>
//                                             <select
//                                                 name={`retentionProofCollected${block}`}
//                                                 value={form[`retentionProofCollected${block}`]}
//                                                 onChange={handleFormChange}
//                                             >
//                                                 <option value="">Select</option>
//                                                 <option value="true">Yes</option>
//                                                 <option value="false">No</option>
//                                             </select>
//                                         </div>
//                                     </div>
//                                 </div>
//                             );
//                         })}

//                         <div className="modal-actions">
//                             <button
//                                 className="btn"
//                                 onClick={() => {
//                                     setModalOpen(false);
//                                     resetForm();
//                                 }}
//                             >
//                                 Cancel
//                             </button>
//                             <button className="btn btn-primary" onClick={saveCandidateRetention} disabled={saving}>
//                                 {saving ? "Saving..." : "Save"}
//                             </button>
//                         </div>
//                     </div>
//                 </div>,
//                 document.body
//             )}

//             <style>{`
// .cmp-page{
// padding:22px;
// background:#f7f7fb;
// min-height:100vh;
// font-family:Inter
// }

// .header{
// display:flex;
// justify-content:space-between;
// align-items:center;
// gap:12px
// }

// h1{
// font-size:26px;
// font-weight:900
// }

// .flash{
// padding:10px;
// border-radius:10px;
// margin:10px 0;
// font-weight:700
// }

// .flash-ok{
// background:#ecfdf5;
// color:#065f46
// }

// .flash-err{
// background:#fef2f2;
// color:#991b1b
// }

// .card{
// background:#fff;
// border:1px solid #e5e7eb;
// border-radius:14px;
// padding:16px;
// margin-bottom:14px
// }

// .grid-3{
// display:grid;
// grid-template-columns:1fr auto auto;
// gap:10px
// }

// .btn{
// border:1px solid #e5e7eb;
// padding:10px 14px;
// border-radius:12px;
// font-weight:800;
// cursor:pointer;
// background:#fff
// }

// .btn:disabled{
// opacity:.65;
// cursor:not-allowed
// }

// .btn-primary{
// background:#2563eb;
// color:#fff
// }

// .btn-outline{
// background:#fff
// }

// .btn-sm{
// padding:6px 10px;
// font-size:12px;
// margin-right:8px
// }

// .btn-danger{
// color:#991b1b;
// border-color:#fca5a5
// }

// .search{
// margin-bottom:10px;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb;
// width:100%
// }

// .table-wrap{
// overflow-x:auto
// }

// .ep-table{
// width:100%;
// min-width:2400px;
// border-collapse:collapse
// }

// th,td{
// padding:10px;
// border-bottom:1px solid #e5e7eb;
// font-size:13px;
// white-space:nowrap;
// vertical-align:top
// }

// th{
// background:#fcfcff;
// text-align:left
// }

// td.actions{
// position:sticky;
// right:0;
// background:#fff
// }

// .mini{
// margin-top:4px;
// font-size:11px;
// color:#6b7280;
// font-weight:700
// }

// .muted{
// color:#6b7280;
// font-weight:700;
// text-align:center
// }

// .modal-overlay{
// position:fixed;
// inset:0;
// background:rgba(0,0,0,.45);
// display:flex;
// align-items:center;
// justify-content:center;
// z-index:99999
// }

// .ep-modal{
// background:#fff;
// padding:18px;
// border-radius:16px;
// width:520px;
// max-height:90vh;
// overflow:auto
// }

// .ep-modal.wide{
// width:min(1180px,92vw)
// }

// .modal-title{
// font-size:18px;
// font-weight:900;
// margin-bottom:14px
// }

// .section-card{
// border:1px solid #e5e7eb;
// border-radius:14px;
// padding:14px;
// margin-bottom:14px;
// background:#fafbff
// }

// .section-title{
// font-size:15px;
// font-weight:900;
// margin-bottom:12px
// }

// .form-grid{
// display:grid;
// grid-template-columns:repeat(2, minmax(0, 1fr));
// gap:12px
// }

// .form-grid.one{
// grid-template-columns:1fr;
// margin-bottom:14px
// }

// .field{
// display:flex;
// flex-direction:column;
// gap:6px
// }

// .field label{
// font-size:12px;
// font-weight:800;
// color:#374151
// }

// .ep-modal input,
// .ep-modal select{
// width:100%;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb
// }

// .modal-actions{
// display:flex;
// justify-content:flex-end;
// gap:10px;
// margin-top:16px
// }

// @media (max-width: 768px){
//   .grid-3{
//     grid-template-columns:1fr
//   }

//   .form-grid{
//     grid-template-columns:1fr
//   }

//   .btn-sm{
//     margin-right:6px;
//     margin-bottom:6px
//   }
// }
// `}</style>
//         </div>
//     );
// }
